import { useState, useMemo } from 'react';
import {
  LineChart as LineChartIcon,
  Plus, Edit3, Trash2, RefreshCw, Check, Search, X,
  AlertTriangle, Shield, TrendingUp, Link2, ArrowUpDown,
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { fmtUSD } from '../../utils/formatters';
import GlassSelect from '../Common/GlassSelect';

// ── localStorage keys for Manage Accounts UI state ──────────────────────────
// Persisted so a user's filter / sort preferences survive reloads. Pattern
// matches journifiSync's selectedAccounts/filterMode keys.
const SORT_KEY = 'journifi.accountsTab.sortMode';
const STATUS_KEY = 'journifi.accountsTab.statusFilter';

// Default sort + status filter for fresh users. "name" is the predictable
// default; "all" statuses means show everything (Active + Passed + Blown +
// Cancelled). Users with 50+ accounts will lean on sort and status.
const DEFAULT_SORT = 'name';
const DEFAULT_STATUS = ['Active', 'Passed', 'Blown', 'Cancelled'];

const loadSort = () => {
  try {
    const v = localStorage.getItem(SORT_KEY);
    return v || DEFAULT_SORT;
  } catch { return DEFAULT_SORT; }
};
const saveSort = (v) => { try { localStorage.setItem(SORT_KEY, v); } catch { /* no-op */ } };

const loadStatusFilter = () => {
  try {
    const raw = localStorage.getItem(STATUS_KEY);
    if (!raw) return DEFAULT_STATUS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : DEFAULT_STATUS;
  } catch { return DEFAULT_STATUS; }
};
const saveStatusFilter = (v) => { try { localStorage.setItem(STATUS_KEY, JSON.stringify(v)); } catch { /* no-op */ } };

const formatDrawdownType = (type) => {
  switch (type) {
    case 'static': return 'Static';
    case 'eodTrailing': return 'EOD Trailing';
    case 'liveTrailing': return 'Live Trailing';
    default: return 'Static';
  }
};

/**
 * AccountsTab — Account Performance + full Account Management (v7.5.15).
 *
 * Previously the management UI was a modal-on-modal (Settings → General →
 * AccountManagementModal). That created a z-index bug where the parent
 * Settings modal's footer intercepted clicks on the bottom-row management
 * buttons. Migration moves the full card grid here, adds search / sort /
 * status filter for users with many accounts, and repoints the old "Manage
 * Accounts" button in Settings to close + switch to this tab.
 */
const AccountsTab = ({
  darkMode,
  accounts,
  closedFilteredTrades,
  selectedAccountFilter,
  setSelectedAccountFilter,
  activeAccountsOnly,
  includePassedInStats,
  getAccountById,
  getAccountStats,
  getAccountBalance,
  setShowSettings,
  // ── Management surface (migrated from AccountManagementModal) ───────────
  onAddAccount,
  onEditAccount,
  onDeleteAccount,
  onMarkBlown,
  onResetAccount,
  onCancelAccount,
  onUpdateAccount,
  getAccountCommissions,
  getAccountResetFees,
  getAccountHighBalance,
  getAccountDrawdownThreshold,
  getAccountDrawdownCushion,
  trackResetFees,
  defaultResetFee,
  journifiSync,
  onBackfillAccounts,
}) => {
  // Local UI state for the management section.
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortModeState] = useState(loadSort);
  const [statusFilter, setStatusFilterState] = useState(loadStatusFilter);
  const [backfillResult, setBackfillResult] = useState(null);

  const setSortMode = (v) => { setSortModeState(v); saveSort(v); };
  const setStatusFilter = (v) => { setStatusFilterState(v); saveStatusFilter(v); };

  // Reverse-lookup: which NT account names map to this journal account?
  // Used by the NT Link row on each card.
  const getMappedNTAccounts = (accountId) => {
    if (!journifiSync?.accountMappings) return [];
    return Object.entries(journifiSync.accountMappings)
      .filter(([_, jId]) => jId === accountId)
      .map(([ntName]) => ntName);
  };

  const handleBackfill = () => {
    if (onBackfillAccounts) {
      const result = onBackfillAccounts();
      setBackfillResult(result);
      setTimeout(() => setBackfillResult(null), 5000);
    }
  };

  // ── derived: visible accounts after search + status filter + sort ──────
  // Memoized against the inputs so re-renders triggered by unrelated state
  // (modal opens elsewhere, etc.) don't re-sort 50 cards.
  const visibleAccounts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = accounts.filter(a => {
      // Status filter — empty array means "hide everything" (don't render).
      // Status absence on the account is treated as Active to match the
      // legacy default.
      const status = a.status || 'Active';
      if (!statusFilter.includes(status)) return false;
      if (!q) return true;
      const name = String(a.name || '').toLowerCase();
      const id = String(a.id || '').toLowerCase();
      const broker = String(a.broker || '').toLowerCase();
      return name.includes(q) || id.includes(q) || broker.includes(q);
    });

    // Sort
    const cmp = (a, b) => {
      switch (sortMode) {
        case 'balance': {
          const ba = getAccountBalance(a.id) || 0;
          const bb = getAccountBalance(b.id) || 0;
          return bb - ba; // high → low
        }
        case 'pnl-desc': {
          const pa = (getAccountStats(a.id)?.totalPnL || 0) - (getAccountCommissions?.(a.id) || 0);
          const pb = (getAccountStats(b.id)?.totalPnL || 0) - (getAccountCommissions?.(b.id) || 0);
          return pb - pa; // winners first
        }
        case 'pnl-asc': {
          const pa = (getAccountStats(a.id)?.totalPnL || 0) - (getAccountCommissions?.(a.id) || 0);
          const pb = (getAccountStats(b.id)?.totalPnL || 0) - (getAccountCommissions?.(b.id) || 0);
          return pa - pb; // losers first ("attention view")
        }
        case 'cushion': {
          // Closest to blown surfaces first — risk view. Accounts without
          // drawdown tracking go to the end (Infinity cushion).
          const ca = getAccountDrawdownCushion?.(a.id);
          const cb = getAccountDrawdownCushion?.(b.id);
          const va = ca == null ? Infinity : ca;
          const vb = cb == null ? Infinity : cb;
          return va - vb;
        }
        case 'name':
        default:
          return (a.name || a.id).localeCompare(b.name || b.id);
      }
    };
    return [...result].sort(cmp);
  }, [accounts, searchQuery, statusFilter, sortMode, getAccountBalance, getAccountStats, getAccountCommissions, getAccountDrawdownCushion]);

  const toggleStatus = (status) => {
    setStatusFilter(
      statusFilter.includes(status)
        ? statusFilter.filter(s => s !== status)
        : [...statusFilter, status]
    );
  };
  return (
    <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
      <h2 className={`text-xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Account Performance
      </h2>

      {accounts.length === 0 ? (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <LineChartIcon size={48} className="mx-auto mb-4 opacity-50" />
          <p className="mb-4">No accounts configured yet.</p>
          <button
            onClick={() => setShowSettings(true)}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.3)]'
                : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
            }`}
          >
            Add Your First Account
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Account Selector for Chart */}
          <div className="flex items-center gap-4">
            <label className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Select Account:
            </label>
            <GlassSelect
              value={selectedAccountFilter}
              onChange={(v) => setSelectedAccountFilter(v)}
              options={[
                { value: 'all', label: 'All Accounts Combined' },
                ...[...accounts]
                  .filter(account => !activeAccountsOnly || account.status === 'Active' || (includePassedInStats && account.status === 'Passed'))
                  .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
                  .map(account => ({
                    value: account.id,
                    label: `${account.name || account.id} (${account.status})`,
                  })),
              ]}
              darkMode={darkMode}
              wrapperClassName="relative inline-block"
              className="w-64"
            />
          </div>

          {/* Equity Curve Chart */}
          {(() => {
            // Build equity curve data
            const selectedAccount = selectedAccountFilter !== 'all'
              ? getAccountById(selectedAccountFilter)
              : null;

            // Filter accounts based on activeAccountsOnly toggle
            const filteredAccounts = activeAccountsOnly
              ? accounts.filter(a => a.status === 'Active' || (includePassedInStats && a.status === 'Passed'))
              : accounts;

            const startingBalance = selectedAccount
              ? selectedAccount.startingBalance
              : filteredAccounts.reduce((sum, acc) => sum + acc.startingBalance, 0);

            // Use closedFilteredTrades (excludes open/in-progress trades) for equity curve
            const relevantTrades = selectedAccountFilter === 'all'
              ? closedFilteredTrades.filter(t => t.accountId && filteredAccounts.find(a => a.id === t.accountId))
              : closedFilteredTrades;

            // Sort trades by trading day — string compare, no UTC drift.
            const sortedTrades = [...relevantTrades].sort((a, b) => a.date.localeCompare(b.date));

            // Build cumulative equity data
            let runningBalance = startingBalance;
            const equityData = [
              { date: 'Start', balance: startingBalance, pnl: 0 }
            ];

            // Group by date for daily equity
            const dailyPnL = {};
            sortedTrades.forEach(trade => {
              dailyPnL[trade.date] = (dailyPnL[trade.date] || 0) + (parseFloat(trade.pnl) || 0);
            });

            Object.keys(dailyPnL).sort().forEach(date => {
              runningBalance += dailyPnL[date];
              equityData.push({
                date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                fullDate: date,
                balance: runningBalance,
                pnl: dailyPnL[date]
              });
            });

            const currentBalance = runningBalance;
            const totalPnL = currentBalance - startingBalance;
            const peakBalance = equityData.reduce((peak, point, idx) => {
              const maxSoFar = Math.max(...equityData.slice(0, idx + 1).map(d => d.balance));
              return Math.max(peak, maxSoFar - point.balance);
            }, 0);

            return (
              <>
                {/* Account Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Starting Balance</div>
                    <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      ${startingBalance.toLocaleString()}
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current Balance</div>
                    <div className={`text-xl font-bold ${currentBalance >= startingBalance ? 'text-green-500' : 'text-red-500'}`}>
                      ${currentBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total P&L</div>
                    <div className={`text-xl font-bold ${totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {totalPnL >= 0 ? '+' : '-'}${Math.abs(totalPnL).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max Drawdown</div>
                    <div className={`text-xl font-bold text-red-500`}>
                      -${peakBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                {/* Equity Curve */}
                {equityData.length > 1 ? (
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h3 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Equity Curve
                    </h3>
                    <div style={{ width: '100%', height: 600 }}>
                      <ResponsiveContainer>
                        <RechartsLineChart data={equityData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                          <XAxis
                            dataKey="date"
                            stroke={darkMode ? '#9ca3af' : '#6b7280'}
                            fontSize={12}
                          />
                          <YAxis
                            stroke={darkMode ? '#9ca3af' : '#6b7280'}
                            fontSize={12}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                            domain={['dataMin - 500', 'dataMax + 500']}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                              border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              color: darkMode ? '#ffffff' : '#000000'
                            }}
                            formatter={(value, name) => [
                              `$${(parseFloat(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                              name === 'balance' ? 'Balance' : 'Daily P&L'
                            ]}
                          />
                          <ReferenceLine
                            y={startingBalance}
                            stroke={darkMode ? '#6b7280' : '#9ca3af'}
                            strokeDasharray="5 5"
                            label={{
                              value: 'Starting',
                              position: 'right',
                              fill: darkMode ? '#9ca3af' : '#6b7280',
                              fontSize: 12
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="balance"
                            stroke={currentBalance >= startingBalance ? '#10b981' : '#ef4444'}
                            strokeWidth={2}
                            dot={{ fill: darkMode ? '#1f2937' : '#ffffff', strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 6, strokeWidth: 2 }}
                          />
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No trades recorded for this account yet. Add trades to see your equity curve!
                  </div>
                )}

                {/* ── Manage Accounts (migrated from AccountManagementModal) ── */}
                {/* Always-on section — no longer gated on `selectedAccountFilter === 'all'`.
                    Replaces the previous read-only "All Accounts Overview" with the
                    full management UI (edit, delete, drawdown tracking, action buttons).
                    Search / sort / status filter let the section scale to 50+ accounts
                    without becoming unscannable. */}
                <div className="mt-8 pt-6 border-t border-white/5">
                  {/* Section header — title + Add / Re-link buttons */}
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Manage Accounts
                      <span className={`ml-2 text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({visibleAccounts.length} of {accounts.length})
                      </span>
                    </h3>
                    <div className="ml-auto flex items-center gap-2">
                      {onBackfillAccounts && (
                        <button
                          onClick={handleBackfill}
                          className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors duration-300 ${
                            backfillResult
                              ? (backfillResult.updated > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400')
                              : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title="Re-link trades that have no account assigned"
                        >
                          {backfillResult ? (
                            <>
                              <Check size={16} />
                              {backfillResult.updated > 0
                                ? `Linked ${backfillResult.updated} of ${backfillResult.total} trades`
                                : 'No orphaned trades found'}
                            </>
                          ) : (
                            <>
                              <RefreshCw size={16} />
                              Re-link Trades
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={onAddAccount}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                          darkMode
                            ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.3)]'
                            : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                        }`}
                      >
                        <Plus size={18} />
                        Add Account
                      </button>
                    </div>
                  </div>

                  {/* Filter row — search + sort + status chips. Persists to localStorage. */}
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {/* Search */}
                    <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border min-w-[200px] flex-1 max-w-xs ${darkMode ? 'bg-gray-800/60 border-white/10' : 'bg-white border-gray-300'}`}>
                      <Search size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search name / id / broker…"
                        className={`flex-1 bg-transparent text-sm outline-none ${darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                      />
                      {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          <X size={12} />
                        </button>
                      )}
                    </div>

                    {/* Sort */}
                    <div className="flex items-center gap-2">
                      <ArrowUpDown size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                      <GlassSelect
                        value={sortMode}
                        onChange={(v) => setSortMode(v)}
                        options={[
                          { value: 'name', label: 'Name (A-Z)' },
                          { value: 'balance', label: 'Balance (High → Low)' },
                          { value: 'pnl-desc', label: 'P&L (High → Low)' },
                          { value: 'pnl-asc', label: 'P&L (Low → High)' },
                          { value: 'cushion', label: 'Drawdown Cushion (Low → High)' },
                        ]}
                        darkMode={darkMode}
                        ariaLabel="Sort accounts"
                        wrapperClassName="relative inline-block"
                        className="w-56"
                      />
                    </div>

                    {/* Status filter chips */}
                    <div className="flex items-center gap-1.5">
                      {['Active', 'Passed', 'Blown', 'Cancelled'].map(status => {
                        const isOn = statusFilter.includes(status);
                        const activeClass = {
                          Active:    darkMode ? 'bg-green-500/25 text-green-200 border-green-400/50'  : 'bg-green-100 text-green-800 border-green-300',
                          Passed:    darkMode ? 'bg-blue-500/25 text-blue-200 border-blue-400/50'    : 'bg-blue-100 text-blue-800 border-blue-300',
                          Blown:     darkMode ? 'bg-red-500/25 text-red-200 border-red-400/50'      : 'bg-red-100 text-red-800 border-red-300',
                          Cancelled: darkMode ? 'bg-gray-500/25 text-gray-200 border-gray-400/50'    : 'bg-gray-200 text-gray-800 border-gray-300',
                        }[status];
                        const inactiveClass = darkMode
                          ? 'bg-transparent text-gray-400 border-transparent hover:text-gray-200'
                          : 'bg-transparent text-gray-500 border-transparent hover:text-gray-800';
                        return (
                          <button
                            key={status}
                            type="button"
                            onClick={() => toggleStatus(status)}
                            className={`text-xs font-medium px-2.5 py-1 rounded-md border transition-colors duration-150 ${isOn ? activeClass : inactiveClass}`}
                            title={isOn ? `Hide ${status}` : `Show ${status}`}
                          >
                            {status}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Cards grid */}
                  {accounts.length === 0 ? (
                    <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <p className="text-lg mb-2">No accounts added yet</p>
                      <p className="text-sm">Click &ldquo;Add Account&rdquo; to create your first trading account.</p>
                    </div>
                  ) : visibleAccounts.length === 0 ? (
                    <div className={`text-center py-8 rounded-lg border border-dashed ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
                      <p className="text-sm italic">
                        No accounts match the current filters.
                        {searchQuery && <> Try clearing &ldquo;{searchQuery}&rdquo;</>}
                        {statusFilter.length < 4 && <> or enabling more status chips.</>}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {visibleAccounts.map(account => {
                        const stats = getAccountStats(account.id);
                        const balance = getAccountBalance(account.id);
                        const commissions = getAccountCommissions ? getAccountCommissions(account.id) : 0;
                        const highBalance = getAccountHighBalance ? getAccountHighBalance(account.id) : null;
                        const threshold = getAccountDrawdownThreshold ? getAccountDrawdownThreshold(account.id) : null;
                        const cushion = getAccountDrawdownCushion ? getAccountDrawdownCushion(account.id) : null;
                        const maxDrawdown = parseFloat(account.maxDrawdown) || 0;

                        const isWarning = cushion !== null && maxDrawdown > 0 && cushion < maxDrawdown * 0.3;
                        const isDanger  = cushion !== null && maxDrawdown > 0 && cushion < maxDrawdown * 0.15;

                        return (
                          <div
                            key={account.id}
                            className={`p-4 rounded-lg border transition-colors duration-300 ${
                              darkMode ? 'bg-gray-700/60 border-gray-600/50' : 'bg-gray-50 border-gray-200'
                            } ${isDanger ? 'ring-2 ring-red-500' : isWarning ? 'ring-2 ring-yellow-500' : ''}`}
                          >
                            {/* Header — name + status pill + edit/delete */}
                            <div className="flex justify-between items-start mb-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {account.name || account.id}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                                    account.status === 'Active' ? 'bg-green-500/20 text-green-500' :
                                    account.status === 'Blown' ? 'bg-red-500/20 text-red-500' :
                                    account.status === 'Passed' ? 'bg-blue-500/20 text-blue-500' :
                                    account.status === 'Cancelled' ? 'bg-gray-500/20 text-gray-500' :
                                    'bg-yellow-500/20 text-yellow-500'
                                  }`}>
                                    {account.status}
                                  </span>
                                </div>
                                <div className={`text-xs mt-1 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {account.id}
                                </div>
                                <div className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {account.broker || 'No broker'} • {account.type}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {/* Edit / Delete icon buttons — subtle frosted
                                    glass: neutral at rest, blue/red glass on
                                    hover so the affordance matches the rest
                                    of the v7.5.15 UI. */}
                                <button
                                  onClick={() => onEditAccount?.(account)}
                                  className={`p-1.5 rounded-md border border-transparent transition-[background-color,border-color,box-shadow,color] duration-150 ${
                                    darkMode
                                      ? 'text-gray-400 hover:bg-blue-500/25 hover:border-blue-400/50 hover:text-blue-100 hover:ring-1 hover:ring-inset hover:ring-blue-400/25'
                                      : 'text-gray-500 hover:bg-blue-100 hover:border-blue-300 hover:text-blue-700'
                                  }`}
                                  title="Edit Account"
                                >
                                  <Edit3 size={16} />
                                </button>
                                <button
                                  onClick={() => onDeleteAccount?.(account.id)}
                                  className={`p-1.5 rounded-md border border-transparent transition-[background-color,border-color,box-shadow,color] duration-150 ${
                                    darkMode
                                      ? 'text-gray-400 hover:bg-red-500/25 hover:border-red-400/50 hover:text-red-100 hover:ring-1 hover:ring-inset hover:ring-red-400/25'
                                      : 'text-gray-500 hover:bg-red-100 hover:border-red-300 hover:text-red-700'
                                  }`}
                                  title="Delete Account"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>

                            {/* Stats — 2×2 grid */}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div className={`p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-white'}`}>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Balance</div>
                                <div className={`font-semibold ${balance >= account.startingBalance ? 'text-green-500' : 'text-red-500'}`}>
                                  ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                              </div>
                              <div className={`p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-white'}`}>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>P&amp;L (after comm.)</div>
                                <div className={`font-semibold ${(stats.totalPnL - commissions) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {(stats.totalPnL - commissions) >= 0 ? '+' : '-'}${fmtUSD(stats.totalPnL - commissions)}
                                </div>
                              </div>
                              <div className={`p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-white'}`}>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Trades</div>
                                <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {stats.totalTrades}
                                </div>
                              </div>
                              <div className={`p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-white'}`}>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Win Rate</div>
                                <div className={`font-semibold ${stats.winRate >= 50 ? 'text-green-500' : stats.winRate > 0 ? 'text-red-500' : (darkMode ? 'text-white' : 'text-gray-900')}`}>
                                  {stats.winRate.toFixed(1)}%
                                </div>
                              </div>
                            </div>

                            {/* Drawdown tracking */}
                            {maxDrawdown > 0 && threshold !== null && (
                              <div className={`mb-3 p-3 rounded-lg ${
                                isDanger
                                  ? (darkMode ? 'bg-red-900/30 border border-red-500/50' : 'bg-red-50 border border-red-200')
                                  : isWarning
                                    ? (darkMode ? 'bg-yellow-900/30 border border-yellow-500/50' : 'bg-yellow-50 border border-yellow-200')
                                    : (darkMode ? 'bg-gray-600/60' : 'bg-white')
                              }`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    {isDanger ? <AlertTriangle size={14} className="text-red-500" />
                                      : isWarning ? <AlertTriangle size={14} className="text-yellow-500" />
                                      : <Shield size={14} className={darkMode ? 'text-cyan-400' : 'text-cyan-600'} />}
                                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      {formatDrawdownType(account.drawdownType || 'static')} Drawdown
                                    </span>
                                  </div>
                                  {highBalance && account.drawdownType === 'eodTrailing' && (
                                    <div className="flex items-center gap-1">
                                      <TrendingUp size={12} className="text-green-500" />
                                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        High: ${highBalance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex justify-between items-center text-sm mb-2">
                                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Threshold:</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    ${threshold.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-sm mb-2">
                                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Cushion:</span>
                                  <span className={`font-bold ${isDanger ? 'text-red-500' : isWarning ? 'text-yellow-500' : 'text-green-500'}`}>
                                    ${cushion.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                  <div
                                    className={`h-full rounded-full transition-[background-color,border-color,box-shadow,color] duration-150 ${
                                      isDanger ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min(100, Math.max(0, (cushion / maxDrawdown) * 100))}%` }}
                                  />
                                </div>
                                <div className={`text-xs mt-1 text-center ${
                                  isDanger ? 'text-red-400' : isWarning ? 'text-yellow-400' : (darkMode ? 'text-gray-500' : 'text-gray-400')
                                }`}>
                                  {((cushion / maxDrawdown) * 100).toFixed(0)}% of max drawdown remaining
                                </div>
                              </div>
                            )}

                            {/* Commission */}
                            {account.commissionPerSide > 0 && (
                              <div className={`text-xs mb-3 p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-gray-100'}`}>
                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Commission: </span>
                                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  ${account.commissionPerSide}/side • Total: ${fmtUSD(commissions)}
                                </span>
                              </div>
                            )}

                            {/* Period + reset info */}
                            {(account.currentPeriod > 1 || account.resetHistory?.length > 0) && (
                              <div className={`text-xs mb-3 p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-gray-100'}`}>
                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                                  Period {account.currentPeriod || 1} • {account.resetHistory?.length || 0} resets
                                  {trackResetFees && account.resetHistory?.length > 0 && getAccountResetFees && (
                                    <span className="text-red-400 ml-2">
                                      (${getAccountResetFees(account.id)} in fees)
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* NinjaTrader link status */}
                            {journifiSync && (() => {
                              const mappedNT = getMappedNTAccounts(account.id);
                              return (
                                <div className={`text-xs mb-3 p-2 rounded-sm ${darkMode ? 'bg-gray-600/60' : 'bg-gray-100'}`}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                      <Link2 size={12} className={darkMode ? 'text-blue-400' : 'text-blue-500'} />
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>NT Link:</span>
                                    </div>
                                    {mappedNT.length > 0 ? (
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <span className={`font-mono truncate ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                          {mappedNT.join(', ')}
                                        </span>
                                        <Check size={12} className="text-green-500 shrink-0" />
                                      </div>
                                    ) : (
                                      <span className={`truncate ${darkMode ? 'text-yellow-400/70' : 'text-yellow-600'}`}>
                                        Not linked — use JournifiSync import to map
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Action buttons */}
                            <div className="flex flex-wrap gap-2">
                              {account.status === 'Active' && (
                                <button
                                  onClick={() => onMarkBlown?.(account.id)}
                                  className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded-sm hover:bg-red-500/30 transition-colors"
                                >
                                  Mark Blown
                                </button>
                              )}
                              {(account.status === 'Blown' || account.status === 'Active') && (
                                <button
                                  onClick={() => onResetAccount?.(account.id, defaultResetFee)}
                                  className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded-sm hover:bg-yellow-500/30 transition-colors"
                                >
                                  Reset Account
                                </button>
                              )}
                              {account.status !== 'Cancelled' && (
                                <button
                                  onClick={() => onCancelAccount?.(account.id)}
                                  className="px-2 py-1 text-xs bg-gray-500/20 text-gray-400 rounded-sm hover:bg-gray-500/30 transition-colors"
                                >
                                  Cancel
                                </button>
                              )}
                              {account.status === 'Cancelled' && (
                                <button
                                  onClick={() => onUpdateAccount?.(account.id, { status: 'Active' })}
                                  className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-sm hover:bg-green-500/30 transition-colors"
                                >
                                  Reactivate
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default AccountsTab;
